import { MoreHorizontal, Trash2 } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.tsx';
import type { UsersTableRow } from './types.ts';

/** The dialogs a row action can open. */
export type UserRowActionKind =
  | 'edit'
  | 'resendWelcomeEmail'
  | 'resetPassword'
  | 'generateTempPassword'
  | 'disable'
  | 'permanentDelete';

/**
 * What the signed-in operator may do, resolved once per table from
 * `usePermissions().hasPermission`.
 *
 * ## Permission matrix
 *
 * Every item is gated on a `Permission` from `packages/shared/src/permissions.ts`
 * — never on the mount (`/users` vs a customer page) and never on the target
 * user's account type. Before this table, only "Generate temporary password"
 * was gated client-side and the rest failed with a toast.
 *
 * | Action                     | Permission                  | Extra visible/disabled rule                          |
 * | -------------------------- | --------------------------- | ---------------------------------------------------- |
 * | Create User (toolbar)      | `CREATE_USER`               | —                                                    |
 * | Edit User                  | `UPDATE_USER`               | identity and access on one screen                    |
 * | Resend welcome email       | `UPDATE_USER`               | only when `userStatus === 'FORCE_CHANGE_PASSWORD'`   |
 * | Reset Password             | `RESET_PASSWORD`            | only when `userStatus === 'CONFIRMED'`               |
 * | Generate temporary password| `REGENERATE_TEMP_PASSWORD`  | `disabled` when federated (Entra owns the password)  |
 * | Disable User               | `DELETE_USER`               | `disabled` when already disabled; label doubles as a readout |
 * | Permanently Delete         | `DELETE_USER`               | hidden when federated                                |
 *
 * Consequence, by design: **Support** holds `VIEW_USERS`, `CREATE_USER`,
 * `UPDATE_USER`, `RESET_PASSWORD`, `REGENERATE_TEMP_PASSWORD` and
 * `MANAGE_CUSTOMER_ASSIGNMENTS` but **not** `DELETE_USER`
 * (`permissions.ts` `ROLE_PERMISSIONS[WellspringRole.SUPPORT]`), so a Support
 * user no longer sees "Disable User" or "Permanently Delete" at all. Admin and
 * SuperAdmin see everything. A Viewer lacks `VIEW_USERS` and never reaches the
 * table.
 */
export interface UsersTablePermissions {
  /** `Permission.CREATE_USER` — the toolbar's Create User button. */
  create: boolean;
  /** `Permission.UPDATE_USER` — Edit User and Resend welcome email. */
  update: boolean;
  /** `Permission.RESET_PASSWORD`. */
  resetPassword: boolean;
  /** `Permission.REGENERATE_TEMP_PASSWORD`. */
  regenerateTempPassword: boolean;
  /** `Permission.DELETE_USER` — Disable User and Permanently Delete. */
  delete: boolean;
}

export interface UserRowActionsProps {
  row: UsersTableRow;
  can: UsersTablePermissions;
  onAction: (kind: UserRowActionKind, row: UsersTableRow) => void;
}

const FEDERATED_TEMP_PASSWORD_TITLE =
  'Federated users sign in via Azure Entra; password regeneration does not apply.';

/**
 * True when at least one item would render. With no permitted action the kebab
 * is not rendered at all, rather than opening onto an empty menu.
 */
export function hasAnyRowAction(row: UsersTableRow, can: UsersTablePermissions): boolean {
  if (can.update) return true;
  if (can.resetPassword && row.userStatus === 'CONFIRMED') return true;
  if (can.regenerateTempPassword) return true;
  if (can.delete) return true;
  return false;
}

/** The per-row kebab menu. Identical on both mounts. */
export function UserRowActions({ row, can, onAction }: UserRowActionsProps) {
  if (!hasAnyRowAction(row, can)) return null;

  const showResendWelcomeEmail = can.update && row.userStatus === 'FORCE_CHANGE_PASSWORD';
  const showResetPassword = can.resetPassword && row.userStatus === 'CONFIRMED';
  const showPermanentDelete = can.delete && !row.isFederated;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='ghost' className='h-7 w-7 p-0'>
          <span className='sr-only'>Open menu</span>
          <MoreHorizontal className='h-4 w-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        {
          /*
          The one edit entry, and the customer page's only edit affordance too:
          `UserEditDialog` shows identity and access on a single screen, so
          editing a role needs no separate item. Not gated on the customer's
          account type, which is what used to hide the customer page's button
          for Utility customers.
        */
        }
        {can.update && (
          <DropdownMenuItem onClick={() => onAction('edit', row)}>
            Edit User
          </DropdownMenuItem>
        )}
        {showResendWelcomeEmail && (
          <DropdownMenuItem onClick={() => onAction('resendWelcomeEmail', row)}>
            Resend welcome email
          </DropdownMenuItem>
        )}
        {showResetPassword && (
          <DropdownMenuItem onClick={() => onAction('resetPassword', row)}>
            Reset Password
          </DropdownMenuItem>
        )}
        {can.regenerateTempPassword && (
          <DropdownMenuItem
            disabled={row.isFederated}
            title={row.isFederated ? FEDERATED_TEMP_PASSWORD_TITLE : undefined}
            onClick={() => onAction('generateTempPassword', row)}
          >
            Generate temporary password
          </DropdownMenuItem>
        )}
        {can.delete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className='text-destructive'
              disabled={!row.enabled}
              onClick={() => onAction('disable', row)}
            >
              {row.enabled ? 'Disable User' : 'User Disabled'}
            </DropdownMenuItem>
          </>
        )}
        {showPermanentDelete && (
          <DropdownMenuItem
            className='text-destructive font-medium'
            onClick={() => onAction('permanentDelete', row)}
          >
            <Trash2 className='h-4 w-4 mr-2' />
            Permanently Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
